import { useEffect, useState } from 'react';
import {
  PhieuXuatDieuChuyen,
  PhieuXuatDieuChuyenChiTiet,
  PhieuXuatDieuChuyenFormData,
  PhieuXuatDieuChuyenListResponse,
  PhieuXuatDieuChuyenSearchParams
} from '@/types/schemas/phieu-xuat-dieu-chuyen.type';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UsePhieuXuatDieuChuyenReturn {
  phieuXuatDieuChuyens: PhieuXuatDieuChuyen[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  addPhieuXuatDieuChuyen: (newPhieuXuatDieuChuyen: PhieuXuatDieuChuyenFormData) => Promise<PhieuXuatDieuChuyen>;
  updatePhieuXuatDieuChuyen: (
    uuid: string,
    updatedPhieuXuatDieuChuyen: PhieuXuatDieuChuyenFormData
  ) => Promise<PhieuXuatDieuChuyen>;
  deletePhieuXuatDieuChuyen: (uuid: string) => Promise<void>;
  refreshPhieuXuatDieuChuyens: () => Promise<void>;
  getPhieuXuatDieuChuyenByCode: (code: string) => Promise<PhieuXuatDieuChuyen | null>;
  getPhieuXuatDieuChuyenById: (uuid: string) => Promise<PhieuXuatDieuChuyen | null>;
  getPhieuXuatDieuChuyenDetail: (uuid: string) => Promise<PhieuXuatDieuChuyenChiTiet[]>;
  getPhieuXuatDieuChuyenByStatus: (status: string) => Promise<PhieuXuatDieuChuyen[]>;
  getPhieuXuatDieuChuyenByDateRange: (fromDate: string, toDate: string) => Promise<PhieuXuatDieuChuyen[]>;
  getPhieuXuatDieuChuyenByWarehouse: (warehouseId: string) => Promise<PhieuXuatDieuChuyen[]>;
  searchPhieuXuatDieuChuyen: (searchParams: PhieuXuatDieuChuyenSearchParams) => Promise<PhieuXuatDieuChuyen[]>;
}

/**
 * Hook for managing PhieuXuatDieuChuyen (Transfer Export Voucher) data
 *
 * This hook provides functions to fetch, create, update, and delete transfer export vouchers,
 * as well as manage their details and search functionality.
 */
export const usePhieuXuatDieuChuyen = (
  initialPhieuXuatDieuChuyens: PhieuXuatDieuChuyen[] = []
): UsePhieuXuatDieuChuyenReturn => {
  const [phieuXuatDieuChuyens, setPhieuXuatDieuChuyens] = useState<PhieuXuatDieuChuyen[]>(initialPhieuXuatDieuChuyens);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { entity } = useAuth();

  const API_ENDPOINT = 'erp/ton-kho/xuat-kho-noi-bo/xuat-kho-dieu-chuyen';

  const fetchPhieuXuatDieuChuyens = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PhieuXuatDieuChuyenListResponse>(`/entities/${entity.slug}/${API_ENDPOINT}/`);
      setPhieuXuatDieuChuyens(response.data.results);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching transfer export vouchers:', error);
      setError(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  const addPhieuXuatDieuChuyen = async (
    newPhieuXuatDieuChuyen: PhieuXuatDieuChuyenFormData
  ): Promise<PhieuXuatDieuChuyen> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<PhieuXuatDieuChuyen>(
        `/entities/${entity.slug}/${API_ENDPOINT}/`,
        newPhieuXuatDieuChuyen
      );
      setPhieuXuatDieuChuyens(prev => [...prev, response.data]);
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Error adding transfer export voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi thêm phiếu xuất điều chuyển');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePhieuXuatDieuChuyen = async (
    uuid: string,
    updatedPhieuXuatDieuChuyen: PhieuXuatDieuChuyenFormData
  ): Promise<PhieuXuatDieuChuyen> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<PhieuXuatDieuChuyen>(
        `/entities/${entity.slug}/${API_ENDPOINT}/${uuid}/`,
        updatedPhieuXuatDieuChuyen
      );
      setPhieuXuatDieuChuyens(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Error updating transfer export voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi cập nhật phiếu xuất điều chuyển');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePhieuXuatDieuChuyen = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/${API_ENDPOINT}/${uuid}/`);
      setPhieuXuatDieuChuyens(prev => prev.filter(item => item.uuid !== uuid));
      setError(null);
    } catch (error: any) {
      console.error('Error deleting transfer export voucher:', error);
      setError(error.message || 'Có lỗi xảy ra khi xóa phiếu xuất điều chuyển');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPhieuXuatDieuChuyenByCode = async (code: string): Promise<PhieuXuatDieuChuyen | null> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuXuatDieuChuyenListResponse>(`/entities/${entity.slug}/${API_ENDPOINT}/`, {
        params: { i_so_ct: code }
      });
      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (error) {
      console.error('Error fetching transfer export voucher by code:', error);
      return null;
    }
  };

  const getPhieuXuatDieuChuyenById = async (uuid: string): Promise<PhieuXuatDieuChuyen | null> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuXuatDieuChuyen>(`/entities/${entity.slug}/${API_ENDPOINT}/${uuid}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer export voucher by ID:', error);
      return null;
    }
  };

  const getPhieuXuatDieuChuyenDetail = async (uuid: string): Promise<PhieuXuatDieuChuyenChiTiet[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<PhieuXuatDieuChuyenChiTiet[]>(
        `/entities/${entity.slug}/${API_ENDPOINT}/${uuid}/chi-tiet/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer export voucher detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const getPhieuXuatDieuChuyenByStatus = async (status: string): Promise<PhieuXuatDieuChuyen[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuXuatDieuChuyenListResponse>(`/entities/${entity.slug}/${API_ENDPOINT}/`, {
        params: { status }
      });
      return response.data.results;
    } catch (error) {
      console.error('Error fetching transfer export vouchers by status:', error);
      return [];
    }
  };

  const getPhieuXuatDieuChuyenByDateRange = async (
    fromDate: string,
    toDate: string
  ): Promise<PhieuXuatDieuChuyen[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuXuatDieuChuyenListResponse>(`/entities/${entity.slug}/${API_ENDPOINT}/`, {
        params: {
          ngay_ct__gte: fromDate,
          ngay_ct__lte: toDate
        }
      });
      return response.data.results;
    } catch (error) {
      console.error('Error fetching transfer export vouchers by date range:', error);
      return [];
    }
  };

  const getPhieuXuatDieuChuyenByWarehouse = async (warehouseId: string): Promise<PhieuXuatDieuChuyen[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const response = await api.get<PhieuXuatDieuChuyenListResponse>(`/entities/${entity.slug}/${API_ENDPOINT}/`, {
        params: { ma_kho: warehouseId }
      });
      return response.data.results;
    } catch (error) {
      console.error('Error fetching transfer export vouchers by warehouse:', error);
      return [];
    }
  };

  const searchPhieuXuatDieuChuyen = async (
    searchParams: PhieuXuatDieuChuyenSearchParams
  ): Promise<PhieuXuatDieuChuyen[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    try {
      const params: any = {};

      // Map search parameters to API parameters
      if (searchParams.fromDate) params.ngay_ct__gte = searchParams.fromDate;
      if (searchParams.toDate) params.ngay_ct__lte = searchParams.toDate;
      if (searchParams.receiptNumber) params.i_so_ct = searchParams.receiptNumber;
      if (searchParams.status) params.status = searchParams.status;
      if (searchParams.warehouseCode) params.ma_kho = searchParams.warehouseCode;
      if (searchParams.ma_vt) params.ma_vt = searchParams.ma_vt;
      if (searchParams.ma_kho_nhap) params.ma_kho_nhap = searchParams.ma_kho_nhap;
      if (searchParams.tu_vi_tri) params.tu_vi_tri = searchParams.tu_vi_tri;
      if (searchParams.den_vi_tri) params.den_vi_tri = searchParams.den_vi_tri;
      if (searchParams.ma_lo) params.ma_lo = searchParams.ma_lo;
      if (searchParams.don_vi) params.don_vi = searchParams.don_vi;
      if (searchParams.trang_thai) params.trang_thai = searchParams.trang_thai;
      if (searchParams.loc_theo_nguoi_sd) params.loc_theo_nguoi_sd = searchParams.loc_theo_nguoi_sd;

      const response = await api.get<PhieuXuatDieuChuyenListResponse>(`/entities/${entity.slug}/${API_ENDPOINT}/`, {
        params
      });
      return response.data.results;
    } catch (error) {
      console.error('Error searching transfer export vouchers:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchPhieuXuatDieuChuyens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    phieuXuatDieuChuyens,
    isLoading,
    isLoadingDetail,
    error,
    addPhieuXuatDieuChuyen,
    updatePhieuXuatDieuChuyen,
    deletePhieuXuatDieuChuyen,
    refreshPhieuXuatDieuChuyens: fetchPhieuXuatDieuChuyens,
    getPhieuXuatDieuChuyenByCode,
    getPhieuXuatDieuChuyenById,
    getPhieuXuatDieuChuyenDetail,
    getPhieuXuatDieuChuyenByStatus,
    getPhieuXuatDieuChuyenByDateRange,
    getPhieuXuatDieuChuyenByWarehouse,
    searchPhieuXuatDieuChuyen
  };
};

export const fetchAllPhieuXuatDieuChuyen = async (
  entitySlug: string,
  params: Record<string, any> = {},
  pageSize = 200
): Promise<PhieuXuatDieuChuyen[]> => {
  let page = 1;
  let all: PhieuXuatDieuChuyen[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<PhieuXuatDieuChuyenListResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.PHIEU_XUAT_DIEU_CHUYEN}/`,
      {
        params: {
          ...params,
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
